import { readableUUID } from './readableUUID';
import { telemetry } from '../telemetry';

/**
 * Generic utilities for handling crmOrgId in URLs regardless of position
 * This supports both patterns:
 * - /automations/crmOrgId
 * - /routing/crmOrgId/tab/groups
 */

/**
 * Removes a specific crmOrgId from the URL path, regardless of its position
 * @param pathname Current URL pathname
 * @param crmOrgIdToRemove The specific crmOrgId to remove
 * @returns New pathname with the crmOrgId removed
 */
export const removeOrgFromPath = (pathname: string, crmOrgIdToRemove: string): string => {
  if (!crmOrgIdToRemove) return pathname;

  // Split path into segments
  const segments = pathname.split('/').filter(Boolean);

  // Remove the specific crmOrgId segment
  const filteredSegments = segments.filter((segment) => segment !== crmOrgIdToRemove);

  // Reconstruct path
  return '/' + filteredSegments.join('/');
};

/**
 * Replaces a specific crmOrgId in the URL path with a new one, regardless of position
 * @param pathname Current URL pathname
 * @param oldCrmOrgId The crmOrgId to replace (readable UUID format from URL)
 * @param newCrmOrgId The new crmOrgId to use (full UUID - will be converted to readable)
 * @returns New pathname with the crmOrgId replaced
 *
 * Examples:
 * - replaceOrgInPath('/automations/abc123', 'abc123', 'full-uuid-here') → '/automations/def456'
 * - replaceOrgInPath('/routing/abc123/tab/groups', 'abc123', 'full-uuid-here') → '/routing/def456/tab/groups'
 */
export const replaceOrgInPath = (
  pathname: string,
  oldCrmOrgId: string,
  newCrmOrgId: string,
): string => {
  if (!oldCrmOrgId || !newCrmOrgId) {
    return pathname;
  }

  // Convert the new UUID to readable format for URL
  const newReadableUUID = readableUUID(newCrmOrgId);
  if (!newReadableUUID) {
    telemetry.captureError(
      new Error('replaceOrgInPath: Failed to convert new UUID to readable format:'),
      {
        newCrmOrgId,
      },
    );
    return pathname;
  }

  const segments = pathname.split('/').filter(Boolean);
  const updatedSegments = segments.map((segment) =>
    segment === oldCrmOrgId ? newReadableUUID : segment,
  );

  return '/' + updatedSegments.join('/');
};

/**
 * Adds a crmOrgId to a URL path after the first segment
 * @param pathname Current URL pathname (e.g., '/agentic/agent/123/chat/456')
 * @param crmOrgId The crmOrgId to add (full UUID - will be converted to readable)
 * @returns New pathname with the crmOrgId inserted (e.g., '/agentic/abc123/agent/123/chat/456')
 *
 * Examples:
 * - addCrmOrgIdToPath('/agentic/agent/123', 'full-uuid-here') → '/agentic/abc123/agent/123'
 * - addCrmOrgIdToPath('/agentic/agent/123/chat/456', 'full-uuid-here') → '/agentic/abc123/agent/123/chat/456'
 */
export const addCrmOrgIdToPath = (pathname: string, crmOrgId?: string): string | undefined => {
  if (!crmOrgId) {
    return;
  }
  const readableCrmOrgId = readableUUID(crmOrgId);
  if (!readableCrmOrgId) {
    return;
  }

  // pathname starts with '/'
  const path = pathname.split('/').filter(Boolean);
  const [base, ...rest] = path;
  return '/' + [base, readableCrmOrgId, ...rest].join('/');
};
